// Gestão de Frota (2026-10-01) — ver services/gpsdrive-fleet-management.service.ts.
// Chaves "fleet-mgmt" ficam fora da persistência do React Query (dados
// pessoais de motorista/locatário) — ver __root.tsx.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fleetManagementService, type FleetExpenseCategory, type FleetOwnerFilter } from "@/services/gpsdrive-fleet-management.service";
import { reportsService } from "@/services/reports.service";
import type { RouteStop, Trip } from "@/types";

export const useFleetAccess = (enabled = true) =>
  useQuery({
    queryKey: ["fleet-mgmt", "access"],
    queryFn: fleetManagementService.access,
    enabled,
    staleTime: 60_000,
    retry: 1,
  });

export const useFleetOwners = (enabled: boolean) =>
  useQuery({
    queryKey: ["fleet-mgmt", "owners"],
    queryFn: fleetManagementService.owners,
    enabled,
    staleTime: 5 * 60_000,
  });

export const useFleetVehicleIds = (owner: FleetOwnerFilter) =>
  useQuery({
    queryKey: ["fleet-mgmt", "vehicles", owner],
    queryFn: () => fleetManagementService.vehicles(owner),
    select: (d) => d.deviceIds.map(String),
    staleTime: 60_000,
  });

export const useFleetSummary = (owner: FleetOwnerFilter, from: string, to: string, deviceId?: string) =>
  useQuery({
    queryKey: ["fleet-mgmt", "summary", owner, from, to, deviceId ?? ""],
    queryFn: () => fleetManagementService.summary(owner, from, to, deviceId),
    staleTime: 30_000,
  });

// Km/tempos do Painel, calculados no servidor. Enquanto o servidor ainda
// calcula ("computing"), consulta de novo a cada 3 s.
export const useFleetServerStats = (owner: FleetOwnerFilter, from: string, to: string, deviceId?: string) =>
  useQuery({
    queryKey: ["fleet-mgmt", "stats", owner, from, to, deviceId ?? ""],
    queryFn: () => fleetManagementService.stats(owner, from, to, deviceId),
    staleTime: 60_000,
    refetchInterval: (query) => (query.state.data?.status === "computing" ? 3_000 : false),
  });

type Entity = "drivers" | "expenses" | "revenues" | "maintenances" | "rentals";

export function useFleetList<E extends Entity>(entity: E, owner: FleetOwnerFilter, extra?: Record<string, string | undefined>) {
  return useQuery({
    queryKey: ["fleet-mgmt", entity, owner, extra ?? {}],
    queryFn: () =>
      fleetManagementService[entity].list(owner, extra) as Promise<
        Awaited<ReturnType<(typeof fleetManagementService)[E]["list"]>>
      >,
    staleTime: 30_000,
  });
}

// Uma mutation por entidade: create/update/remove. Toda escrita invalida a
// entidade + o resumo do painel (custo de manutenção concluída etc.).
export function useFleetMutations(entity: Entity, owner: FleetOwnerFilter) {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ["fleet-mgmt", entity] });
    qc.invalidateQueries({ queryKey: ["fleet-mgmt", "summary"] });
    if (entity === "maintenances") qc.invalidateQueries({ queryKey: ["fleet-mgmt", "expenses"] });
    if (entity === "expenses") qc.invalidateQueries({ queryKey: ["fleet-mgmt", "recurring-expenses"] });
  };
  const svc = fleetManagementService[entity] as {
    create: (o: string, b: object) => Promise<unknown>;
    update: (o: string, id: string, b: object) => Promise<unknown>;
    remove: (o: string, id: string) => Promise<void>;
  };
  return {
    create: useMutation({ mutationFn: (body: object) => svc.create(owner, body), onSuccess: done }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: object }) => svc.update(owner, id, body),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => svc.remove(owner, id), onSuccess: done }),
  };
}

// Km rodados / tempo em movimento / tempo parado vêm dos relatórios do
// rastreamento (Traccar, sessão de quem está logado). Em lotes de 50
// veículos por chamada — a URL leva um deviceId por veículo (ver o 414 da
// tela Veículos, 2026-10-01).
export interface FleetReportData {
  trips: Trip[];
  stops: RouteStop[];
}

const REPORT_BATCH = 50;

export function useFleetReports(deviceIds: string[], fromIso: string, toIso: string, enabled: boolean) {
  return useQuery({
    queryKey: ["fleet-mgmt", "reports", deviceIds.join(","), fromIso, toIso],
    enabled: enabled && deviceIds.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<FleetReportData> => {
      const batches: string[][] = [];
      for (let i = 0; i < deviceIds.length; i += REPORT_BATCH) batches.push(deviceIds.slice(i, i + REPORT_BATCH));
      const trips: Trip[] = [];
      const stops: RouteStop[] = [];
      // 3 lotes por vez — não derruba o servidor de rastreamento com uma frota grande.
      for (let i = 0; i < batches.length; i += 3) {
        const chunk = batches.slice(i, i + 3);
        const results = await Promise.all(
          chunk.map((b) =>
            Promise.all([reportsService.trips(b, fromIso, toIso), reportsService.stops(b, fromIso, toIso)]),
          ),
        );
        for (const [t, s] of results) {
          trips.push(...t);
          stops.push(...s);
        }
      }
      return { trips, stops };
    },
  });
}

// Seta do motorista (2026-10-04): documentos / contratos / recorrência.
export const useFleetDriverDocuments = (owner: FleetOwnerFilter, driverId: string) =>
  useQuery({
    queryKey: ["fleet-mgmt", "driver-docs", driverId],
    queryFn: () => fleetManagementService.driverDocuments(owner, driverId),
  });

// Modelos da frota (aba Configurações) e os que valem pra um motorista
// (os do dono dele).
export const useFleetContractTemplates = (owner: FleetOwnerFilter) =>
  useQuery({
    queryKey: ["fleet-mgmt", "contract-templates", owner],
    queryFn: () => fleetManagementService.contractTemplates(owner),
  });

export const useFleetDriverContractTemplates = (owner: FleetOwnerFilter, driverId: string) =>
  useQuery({
    queryKey: ["fleet-mgmt", "contract-templates", "driver", driverId],
    queryFn: () => fleetManagementService.driverContractTemplates(owner, driverId),
  });

export const useFleetSettings = (owner: FleetOwnerFilter) =>
  useQuery({
    queryKey: ["fleet-mgmt", "settings", owner],
    queryFn: () => fleetManagementService.settings(owner),
  });

export const useFleetDriverContracts = (owner: FleetOwnerFilter, driverId: string) =>
  useQuery({
    queryKey: ["fleet-mgmt", "driver-contracts", driverId],
    queryFn: () => fleetManagementService.driverContracts(owner, driverId),
  });

export const useFleetDriverBilling = (owner: FleetOwnerFilter, driverId: string) =>
  useQuery({
    queryKey: ["fleet-mgmt", "driver-billing", driverId],
    queryFn: () => fleetManagementService.driverBilling(owner, driverId),
  });

// Catálogo de serviços da manutenção (2026-10-14).
// Categorias de despesa e despesas fixas (2026-10-31).
export const useFleetExpenseCategories = (owner: FleetOwnerFilter) =>
  useQuery({
    queryKey: ["fleet-mgmt", "expense-categories", owner],
    queryFn: () => fleetManagementService.expenseCategories(owner),
    staleTime: 5 * 60_000,
  });

export function useFleetExpenseCategoryMutations(owner: FleetOwnerFilter) {
  const qc = useQueryClient();
  const onSuccess = (data: { items: FleetExpenseCategory[] }) => qc.setQueryData(["fleet-mgmt", "expense-categories", owner], data);
  return {
    add: useMutation({ mutationFn: (label: string) => fleetManagementService.addExpenseCategory(owner, label), onSuccess }),
    remove: useMutation({ mutationFn: (key: string) => fleetManagementService.removeExpenseCategory(owner, key), onSuccess }),
  };
}

export const useFleetRecurringExpenses = (owner: FleetOwnerFilter) =>
  useQuery({
    queryKey: ["fleet-mgmt", "recurring-expenses", owner],
    queryFn: () => fleetManagementService.recurringExpenses(owner),
    staleTime: 30_000,
  });

export function useFleetRecurringExpenseMutations(owner: FleetOwnerFilter) {
  const qc = useQueryClient();
  const onSuccess = () => {
    qc.invalidateQueries({ queryKey: ["fleet-mgmt", "recurring-expenses"] });
    qc.invalidateQueries({ queryKey: ["fleet-mgmt", "expenses"] });
    qc.invalidateQueries({ queryKey: ["fleet-mgmt", "summary"] });
  };
  return {
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: object }) => fleetManagementService.updateRecurringExpense(owner, id, body),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: (id: string) => fleetManagementService.deleteRecurringExpense(owner, id), onSuccess }),
  };
}

export const useFleetMaintenanceServices = (owner: FleetOwnerFilter) =>
  useQuery({
    queryKey: ["fleet-mgmt", "maintenance-services", owner],
    queryFn: () => fleetManagementService.maintenanceServices(owner),
    staleTime: 5 * 60_000,
  });

export function useSaveFleetMaintenanceServices(owner: FleetOwnerFilter) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Parameters<typeof fleetManagementService.saveMaintenanceServices>[1]) =>
      fleetManagementService.saveMaintenanceServices(owner, items),
    onSuccess: (data) => qc.setQueryData(["fleet-mgmt", "maintenance-services", owner], data),
  });
}

// Contratante/testemunhas (Configurações → Contrato) e todos os contratos
// de motorista do escopo (aba Contratos) — 2026-10-26.
export const useFleetContractSettings = (owner: FleetOwnerFilter) =>
  useQuery({
    queryKey: ["fleet-mgmt", "contract-settings", owner],
    queryFn: () => fleetManagementService.contractSettings(owner),
  });

export const useFleetAllContracts = (owner: FleetOwnerFilter) =>
  useQuery({
    queryKey: ["fleet-mgmt", "all-contracts", owner],
    queryFn: () => fleetManagementService.allContracts(owner),
  });
